import { Component, computed, inject, signal } from '@angular/core';
import { CijferHistogram, getCijferSize, partitionHistogram } from '../cijfers-overzicht/cijfers-overzicht.component';
import {
	BasicFilterExpression,
	CijferMeasure,
	CompoundFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	ExportDataOptions,
	ExportFilter,
	FilterDynamicSelectionExpression,
	FilterExpression,
	InFilterExpression,
	isBasicFilterExpression,
} from '../../services/data.service';
import { FilterConfigService } from '../../services/filter-config';
import { ColumnOptions, createMeasureColumn, DataRow, getGroupAttributes } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { att, percOfRow } from '../../services/measures';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { Option, generateCssClassForCijfer } from '@cumlaude/shared-utils';
import { get, last, range, sum } from 'lodash-es';
import { Att, AttrPath, Cijferkolomtype, Table } from '@cumlaude/metadata';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { BarInfo } from '../../services/stacked-bars';
import { formatPercent } from '@angular/common';
import { Attributes, BaseDashboardConfig, Selection, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { CijferkolomKeuze, Dossier } from '../../services/weergave-opties';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { MultiAggregator, noAgg, noAgg0, SingleAggregator, sumOver, weightedAverage } from '../../services/aggregation';
import { Observable } from 'rxjs';
import { PartitionMeasure } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { Axis } from '../../services/axis';
import { InstellingBron } from '@cumlaude/service-contract';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { DataCellComponent } from '../../shared/components/table/cells/data-cell/data-cell.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';

interface ToetsDetailsI extends CijferHistogram {
	cf_m_nr_cijfer_afgerond: number;
	cf_m_nr_cijfer: number;
	cf_m_nr_cijfer_vorig_sj: number;
	cf_m_nr_gem_cijfer_andere_vakken: number;
	cf_m_nr_leerlingen: number;
	cf_m_nr_cijfer_mediaan: number;
	cf_m_nr_sectiegemiddelde: number;
	cf_m_nr_plaatsing_advies_score: number;
	cf_m_nr_aantal_toetsen: number;
	cf_m_is_gevuld: number;
	cf_m_nr_label_onvoldoendes: number;
	cf_nm_lichting_vak: any;
	cf_co_kolom: any;
	cf_abb_kolomkop: any;
}

interface ToetsDetailsA extends Attributes {
	cijfer: number | null;
	aantalLeerlingen: number;
	onvoldoendes: number;
	cijferAfgerondOV: any;
	cijferMediaan: number | null;
	aantalCijfers: number;
	cf_nr_label_onvoldoendes: number | null;
}

@Component({
	selector: 'app-toets-details',
	templateUrl: './toets-details.component.html',
	styleUrls: ['./toets-details.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		FilterBarComponent,
		SingleSelectDropdownComponent,
		LegendaComponent,
	],
})
export class ToetsDetailsComponent extends BarchartTableConfig<ToetsDetailsI, ToetsDetailsA> {
	protected readonly dataService = inject(DataService);
	protected readonly filterConfigService = inject(FilterConfigService);

	InstellingBron = InstellingBron;

	factTable = Table.fac_cf_cijfer;

	tableGroups: AttrPath[] = [['cf_nm_vak'], ['cf_fun_nm_leerjaar_niveau_lesgroep'], ['cf_nm_lesgroep_docenten']];

	override groupsAsTitles = [0, 2];

	override fixedBeforeGroups = 4;

	fixedGroups: AttrPath[] = [['cf_fun_nm_periode'], ['cf_fun_nm_kolomgroep'], ['cf_fun_nr_toets_rank'], ['cf_nr_weging'], ['cf_des_kolom']];

	fixedSubgroups: AttrPath[] = [['cf_nm_lichting_vak'], ['cf_co_kolom'], ['cf_abb_kolomkop']];

	actueelFilters: FilterName[] = [
		'cf_nm_schooljaar',
		'cf_co_brin',
		'cf_fk_ilt.ilt_nm_niveau',
		'cf_nr_leerjaar',
		'cf_nm_vestiging',
		'cf_nm_vak',
		'cf_nm_lesgroep',
		'cf_fun_nm_periode',
		'cf_is_alternatievenormering',
	];

	permanentFilterExpressions: FilterExpression[] = [];

	dossier = this.qp.signal('dossier');

	dossierOpties = Object.values(Dossier).map((val) => new Option(val));

	cijferkolomKeuze = this.qp.signal('cijfertype');

	readonly cijferkolomKeuzeOpties = signal<Option<CijferkolomKeuze>[]>(
		[CijferkolomKeuze.ALLE, CijferkolomKeuze.TOETS, CijferkolomKeuze.GEMIDDELDE].map((val) => new Option(val))
	);

	protected override singleAggregators: Partial<{ [ai in keyof ToetsDetailsA]: SingleAggregator<ToetsDetailsI, ToetsDetailsA[ai]> }> = {
		aantalLeerlingen: noAgg0('cf_m_nr_leerlingen'),
		onvoldoendes: {
			init: (i: ToetsDetailsI) => i.cf_m_nr_label_onvoldoendes ?? sum(range(0, 6).map((n) => get(i, 'cf_m_nr_aantal_' + n, 0))),
			combine: (as: number[]) => sum(as),
		},
		aantalCijfers: sumOver('cf_m_is_gevuld'),
		cf_nr_label_onvoldoendes: sumOver('cf_m_nr_label_onvoldoendes'),
		cijferMediaan: noAgg('cf_m_nr_cijfer_mediaan'),
	};

	protected override multiAggregators: MultiAggregator<keyof ToetsDetailsA, ToetsDetailsI, ToetsDetailsA, number | null>[] = [
		weightedAverage('cijfer', 'cf_m_nr_cijfer', 'aantalCijfers'),
	];

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getCijfersData({
			...options,
			m: [CijferMeasure.HISTOGRAM, CijferMeasure.RELATIE, CijferMeasure.STATS],
			r: this.getRollupMarkers(),
		});
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getCijfersExportData(options);
	}

	override getBarchartQty(path: Path<ToetsDetailsA, number[]>): number {
		return Math.min(getLeafA(path).cijfer ?? 0, 10);
	}

	override createXAxis(_context: DashboardContext<ToetsDetailsI, ToetsDetailsA, ToetsDetailsComponent>): Axis {
		return { min: 0, max: 10, ticks: [] };
	}

	override partitionBarData(rowRoot: Level<ToetsDetailsA, number[]>): Path<ToetsDetailsA, number[]>[][] {
		return partitionHistogram(rowRoot);
	}

	partitionMeasure: PartitionMeasure<ToetsDetailsA> = {
		type: 'number',
		getValue: (path) => getLeafA(path).cijfer,
		format: '1.1-1',
	};

	readonly permanentFilters = computed(() => {
		const filters: FilterExpression[] = [];
		if (this.userService.bron() === InstellingBron.Somtoday && this.cijferkolomKeuze() !== CijferkolomKeuze.GEMIDDELDE) {
			filters.push(
				this.dossier() === Dossier.VOORTGANG
					? new BasicFilterExpression(['cf_is_voortgangsdossier'], 1)
					: new BasicFilterExpression(['cf_is_examendossier'], 1)
			);
		}

		switch (this.cijferkolomKeuze()) {
			case CijferkolomKeuze.ALLE:
				filters.push(new BasicFilterExpression(['cf_nm_kolomtype'], Cijferkolomtype.ADVIES, '<>'));
				break;
			case CijferkolomKeuze.TOETS:
				filters.push(new BasicFilterExpression(['cf_nm_kolomtype'], Cijferkolomtype.TOETS, '='));
				break;
			case CijferkolomKeuze.GEMIDDELDE:
				filters.push(
					new InFilterExpression(
						['cf_nm_kolomtype'],
						[
							Cijferkolomtype.PERIODEGEMIDDELDE,
							Cijferkolomtype.GEMIDDELDE,
							Cijferkolomtype.RAPPORTCIJFER,
							Cijferkolomtype.SE_CIJFER,
							Cijferkolomtype.CE_CIJFER,
							Cijferkolomtype.EINDCIJFER,
						].filter((value) => this.filterConfigService.cijferKolomTypeAllowed(value))
					)
				);
				break;
			default:
		}

		return filters;
	});

	override getDisplayOptions(): ExportFilter[] {
		return [
			...(this.userService.bron() === InstellingBron.Somtoday ? [{ label: 'Toetsdossier', value: this.dossier() }] : []),
			{ label: 'Cijfertype', value: this.cijferkolomKeuze() === CijferkolomKeuze.ALLE ? 'Alle' : this.cijferkolomKeuze() },
		];
	}

	protected getRollupMarkers() {
		// 1 = hoogste niveau tables
		// 4 = periode
		// 5 = toets
		// overige groepen hebben geen sortering nodig
		return [0, 1, 4, 5, this.fixedGroups.length + this.tableGroups.length];
	}

	override createMeasureColumns(
		context: DashboardContext<ToetsDetailsI, ToetsDetailsA, ToetsDetailsComponent>
	): ColumnDef<DataRow<ToetsDetailsA>>[] {
		return [
			createMeasureColumn('Gem.', att('cijfer'), { context, format: '1.2-2', headerTooltip: 'Gemiddelde' }),
			createMeasureColumn('Mediaan', att('cijferMediaan'), { context, format: '1.1-1' }),
			createMeasureColumn('% onv.', percOfRow('onvoldoendes', 'count_records'), {
				context,
				dataType: 'percentage',
				headerTooltip: 'Percentage onvoldoendes',
			}),
			this.createLeerlingColumn('lln', att('aantalLeerlingen'), { context, headerTooltip: 'Leerlingen' }),
		];
	}

	override createGroupingColumns(
		context: DashboardContext<ToetsDetailsI, ToetsDetailsA, ToetsDetailsComponent>
	): ColumnDef<DataRow<ToetsDetailsA>>[] {
		const visibleGroups: { att: Att; options?: Partial<ColumnOptions<ToetsDetailsI, ToetsDetailsA>> }[] = [
			{ att: 'cf_fun_nm_periode', options: { header: 'Per.', headerTooltip: 'Periode' } },
			{ att: 'cf_fun_nm_kolomgroep' },
		];
		if (this.cijferkolomKeuze() !== CijferkolomKeuze.GEMIDDELDE) {
			visibleGroups.push(
				{
					att: 'cf_nr_weging',
					options: { header: 'Weg.', headerTooltip: 'Weging', dataType: 'number', component: DataCellComponent, format: '1.0-2' },
				},
				{
					att: 'cf_des_kolom',
					options: { header: 'Naam' },
				}
			);
		}
		return visibleGroups.map(({ att, options }) => {
			const columnDef = this.createGroupingColumn(att, context.groupNames.indexOf(att) + 1, false, false, false, options);
			return {
				...columnDef,
				header: {
					...columnDef.header,
					headerType: 'default',
				},
				sortable: true,
			};
		});
	}

	override getSelectionConfig(context: DataRequestContext<ToetsDetailsA>): SelectionConfig<ToetsDetailsA> | undefined {
		return { ...super.getSelectionConfig(context)!, getSize: att('aantalLeerlingen') };
	}

	override createSelection(
		context: DashboardContext<ToetsDetailsI, ToetsDetailsA, BaseDashboardConfig<ToetsDetailsI, ToetsDetailsA>>,
		path: Path<ToetsDetailsA, number[]>,
		overrideConfig: Partial<SelectionConfig<ToetsDetailsA>> = {}
	): Selection | undefined {
		const selection = super.createSelection(context, path, overrideConfig)!;
		const { sf, sp } = selection.expression as FilterDynamicSelectionExpression;
		const newFilters = (sf as CompoundFilterExpression).filters.filter(
			(f) => !(isBasicFilterExpression(f) && f.attr[0] === 'cf_fun_nr_toets_rank')
		);
		return { ...selection, expression: new FilterDynamicSelectionExpression(sp, new CompoundFilterExpression(newFilters)) };
	}

	override makeBar(
		attrs: ToetsDetailsI,
		path: Path<ToetsDetailsA, number[]>,
		context: DashboardContext<ToetsDetailsI, ToetsDetailsA, ToetsDetailsComponent>
	): BarInfo {
		const { cf_co_kolom, cf_abb_kolomkop, cf_des_kolom, cf_nr_weging, cf_m_nr_leerlingen, cf_nm_lichting_vak } = <
			ToetsDetailsI & { cf_des_kolom: string; cf_nr_weging: number }
		>{
			...attrs,
			...getGroupAttributes(context, path),
		};

		const tooltipInfo: TooltipElement[] = [
			{ label: 'Kolom', value: cf_co_kolom },
			{ label: 'Kolomkop', value: cf_abb_kolomkop },
			{ label: 'Omschrijving', value: cf_des_kolom },
		];

		if (cf_nm_lichting_vak) tooltipInfo.push({ label: 'Lichting (Vak)', value: cf_nm_lichting_vak });

		if (cf_nr_weging !== null) tooltipInfo.push({ label: 'Weging', value: `${cf_nr_weging}` });

		tooltipInfo.push({ label: '# lln', value: `${cf_m_nr_leerlingen}` });

		const perc_onvoldoende = percOfRow('onvoldoendes', 'count_records')(path);
		tooltipInfo.push({ label: 'onvoldoendes', value: formatPercent(perc_onvoldoende, 'nl_NL') });

		const size = getCijferSize(attrs, path);
		const className = generateCssClassForCijfer(last(path)!.a.cijferAfgerondOV);
		return {
			className,
			size,
			tooltip: tooltipInfo,
		};
	}

	override enrichTableModel(
		_context: DashboardContext<ToetsDetailsI, ToetsDetailsA, ToetsDetailsComponent>,
		tableModel: TableModel<DataRow<ToetsDetailsA>>
	) {
		tableModel.showFooters = false;
	}

	getSelectedDossierOptie() {
		return this.dossierOpties.find((optie) => optie.value === this.dossier())!;
	}

	getSelectedCijfertypeOptie() {
		return this.cijferkolomKeuzeOpties().find((optie) => optie.value === this.cijferkolomKeuze())!;
	}
}
